/*
 List of Welders Component:
 1. Displays welders used in specific projects with filtering capabilities from WI 
 2. Generates Excel reports of welder data based on project, joint, date, and status filters
 3. Uses cascading dropdowns: Project → Joint → Welder selection
 4. Exports data to Excel with formatted columns and headers

 Backend Services Used:
 - WIexportreport: Queries WI table with filters (project, joint, welder, date range, status) to get WI slip data for Excel export
 - getListofProjectno: Gets all project numbers with their joint numbers from PROJECT_NO table for dropdown population
 - getWIdataexcelbyProjectno: Gets WI data for specific project+joint combination to populate welder dropdown options


 */
import React, { useState, forwardRef, useEffect } from "react";
import { Row, Col, Container, Button } from "react-bootstrap";
import { showWarning, showInfo, showSuccess } from "../utility/ui/notifications/Toaster";
import { WIexportreport } from "../../Services/WI-services";
import { validateWelderReportForm } from "../../utils/welderReportValidation";
import { ExportToExcel } from "../utility/data/export/PRODWI_Excel";
import WelderReportFilters from "../utility/forms/WelderReportFilters";
import { GridLoader } from "react-spinners";
import { useWelderReportCommon } from "../../hooks/useWelderReportCommon";

const List_Of_Welders = forwardRef((props, ref) => {

  const {
    wiobj, setwiobj, error, setError, Slipstatus,
    getUniqueProjectNumbers, getJointNosForSelectedProject, getWelderForSelectedProject,
    handleProjectChange, handleJointChange, handleWelderChange, handleSlipStatusChange
  } = useWelderReportCommon();


  const [reload, setReload] = useState(false);
  const [loading, setLoading] = useState(false);
  let reporName = "WelderListReport";

  useEffect(() => {
    if (reload) {
      setReload(false);
    }
  }, [reload]);


  // Paginated data fetching for large datasets
  const fetchDataPaginated = async () => {
    const BATCH_SIZE = 1000;
    let allData = [];
    let lastKey = null;
    let hasMore = true;
    let batchCount = 0;

    console.log(`🚀 Starting paginated data fetch for List of Welders`);

    while (hasMore) {
      try {
        const batch = await WIexportreport(wiobj, {
          paginated: true,
          limit: BATCH_SIZE,
          lastKey: lastKey
        });

        allData = [...allData, ...batch.items];
        lastKey = batch.lastKey;
        hasMore = batch.hasMore;
        batchCount++;

      } catch (error) {
        console.error(`Error fetching batch ${batchCount + 1}:`, error);
        throw new Error(`Failed to fetch data batch ${batchCount + 1}: ${error.message}`);
      }
    }
    return allData;
  };

  const handleSubmit = async (e) => {
    if (!validateWelderReportForm(wiobj, ['fromDate', 'toDate', 'slipStatus'])) {
      return;
    }

    setLoading(true);
    try {


      let Data = [];
      console.log("Wiobj", wiobj)
      if (wiobj.WI_From_Date !== "" && wiobj.WI_To_Date !== "") {
        // Estimate if we need pagination based on filters
        // Temporarily force pagination for testing (remove in production)
        const needsPagination = !wiobj.WI_Joint_No && !wiobj.WI_Welder_Id;

        let result;
        if (needsPagination) {
          console.log(`Using paginated fetch (no specific joint/welder filter)`);
          result = await fetchDataPaginated();
        } else {
          console.log(`Using standard fetch (specific filters applied)`);
          result = await WIexportreport(wiobj);
        }
        let sheetName = "Report_ListofWelders";
        let filename = "List of welders used in project";
        if (result?.length > 0) {
          result.forEach((row) => {
            Data.push({
              A: "",
              B: row.WI_Project_No,
              C: row.WI_SlipDate,
              D: row.WI_plant,
              E: row.WI_Slip,
              F: row.WI_Slip_Status == "Cancelled" ? "Yes" : "No",
              G: row.WI_Joint_No,
              H: row.WI_WPS_No,
              I: row.WI_Process,
              J: row.WI_Type,
              K: row.WI_Welder_Id,
              L: row.WI_Welder_Name,
            });
          });

          let titleCell = "E3";
          let titleWelderRange = "G1:Y5";
          let tbodyHeadRange = "A6:L6";
          let tbodyRange = "A7";
          let blankRow = "A5:L5";
          let width = [
            { col: "A", width: 2.5 },
            { col: "B", width: 13.7 },
            { col: "C", width: 13.7 },
            { col: "D", width: 13.7 },
            { col: "E", width: 8.5 },
            { col: "F", width: 9.5 },
            { col: "G", width: 16.5 },
            { col: "H", width: 20 },
            { col: "I", width: 13.7 },
            { col: "J", width: 13.7 },
            { col: "K", width: 15 },
            { col: "L", width: 18 },
          ];
          let Headings = [
            { col: "G2", value: "LIST OF WELDERS USED IN PROJECT" },
            {
              col: "C4",
              value:
                "1.One row displays data for one slip number for entered filter. Slip number is unique.",
            },
          ];

          ExportToExcel(filename, Data, {
            colwidth: width,
            titleCell,
            titleWelderRange,
            tbodyHeadRange,
            tbodyRange,
            blankRow,
            Headings,
            sheetName,
            reportName: reporName
          });
        } else {
          showInfo("There are no records matching to filters.");
        }

        if (result.length > 0) {
          showSuccess("Exported successfully");
        }
      }
    } catch (error) {
      console.error('Error in handleSubmit:', error);
      showWarning('An error occurred while processing the request. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {loading && (
        <div className="loader-overlay">
          <GridLoader size={10} className="pageloader" />
        </div>
      )}
      <Container fluid>
        <Row className="RowHeight mb-4 ml-2">
          <Col md={12} sm={12} xs={12} className="ListTitle">
            List of Welders Used in Project Filters
          </Col>
        </Row>
        <WelderReportFilters
          wiobj={wiobj}
          setwiobj={setwiobj}
          error={error}
          setError={setError}
          uniqueProjectNumbers={getUniqueProjectNumbers()}
          jointOptions={getJointNosForSelectedProject()}
          welderOptions={getWelderForSelectedProject()}
          slipStatusOptions={Slipstatus}
          onProjectChange={handleProjectChange}
          onJointChange={handleJointChange}
          onWelderChange={handleWelderChange}
          onSlipStatusChange={handleSlipStatusChange}
          showFileFormat={false}>
        </WelderReportFilters>
        <Row>
          <Col>
            <Button
              onClick={handleSubmit}
              disabled={loading}
              className="btn-add-color semiBoldToggleButton rightAlignedButton"
            >
              {loading ? 'Processing...' : 'Submit'}
            </Button>
          </Col>
        </Row>
      </Container>


    </>
  );
});

export default List_Of_Welders;
